import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Badge } from 'react-bootstrap';
import Sidebar from '../../components/sidebar/Sidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Money, Username } from '../../utils/privacyMask';
import { getPackageColor, getPackageName, getPackagePrice, getPackageProfit, getPackageTitle } from '../../utils/packages';
import './CalendarPage.css';

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function fmtMoney(n) { return `€${(parseFloat(n) || 0).toFixed(2)}`; }

export default function CalendarPage() {
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selected, setSelected] = useState(null); // day number

  const [subscriptions, setSubscriptions] = useState([]);
  const [accountsMap,   setAccountsMap]   = useState({});
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [subRes, accRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/subscription?all=true`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`,              { headers }),
        ]);
        setSubscriptions(subRes.data || []);
        const m = {};
        accRes.data.forEach(a => { m[a._id] = a.username; });
        setAccountsMap(m);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load calendar');
        setLoading(false);
      }
    })();
  }, []);

  /* ── Navigation ── */
  const prevMonth = () => {
    setSelected(null);
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    setSelected(null);
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); setSelected(null); };

  /* ── Group subscriptions by expiry day within this month ── */
  const dayMap = useMemo(() => {
    const m = {};
    subscriptions.forEach(sub => {
      const d = new Date(Number(sub.expireDate));
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (!m[day]) m[day] = [];
        m[day].push(sub);
      }
    });
    return m;
  }, [subscriptions, year, month]);

  /* ── Build calendar cells (Mon–Sun grid) ── */
  const cells = useMemo(() => {
    const result = [];
    const firstDow   = new Date(year, month, 1).getDay();        // 0=Sun
    const offset     = (firstDow + 6) % 7;                       // Mon-first offset
    const daysInMon  = new Date(year, month + 1, 0).getDate();
    const daysInPrev = new Date(year, month, 0).getDate();

    for (let i = offset - 1; i >= 0; i--)
      result.push({ day: daysInPrev - i, current: false, subs: [] });

    for (let d = 1; d <= daysInMon; d++)
      result.push({ day: d, current: true, isToday: year === now.getFullYear() && month === now.getMonth() && d === now.getDate(), subs: dayMap[d] || [] });

    const trailing = (7 - (result.length % 7)) % 7;
    for (let d = 1; d <= trailing; d++)
      result.push({ day: d, current: false, subs: [] });

    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, dayMap]);

  /* ── Selected day data ── */
  const selectedSubs = selected ? (dayMap[selected] || []) : [];

  const selectedTotals = useMemo(() => ({
    predRevenue: selectedSubs.reduce((s, sub) => s + (getPackagePrice(sub.package)  || 0), 0),
    predProfit:  selectedSubs.reduce((s, sub) => s + (getPackageProfit(sub.package) || 0), 0),
    actRevenue:  selectedSubs.reduce((s, sub) => s + (parseFloat(sub.paidPrice)      || 0), 0),
    actProfit:   selectedSubs.reduce((s, sub) => s + (parseFloat(sub.profitReceived) || 0), 0),
  }), [selectedSubs]);

  /* ── Month totals ── */
  const monthTotals = useMemo(() => {
    const allSubs = Object.values(dayMap).flat();
    return {
      count:       allSubs.length,
      predRevenue: allSubs.reduce((s, sub) => s + (getPackagePrice(sub.package)  || 0), 0),
      predProfit:  allSubs.reduce((s, sub) => s + (getPackageProfit(sub.package) || 0), 0),
    };
  }, [dayMap]);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Calendar" />
      <div className="cal-page">

        {error && (
          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}

        {/* ── Navigation ── */}
        <div className="cal-nav">
          <button className="cal-nav-btn" onClick={prevMonth}><i className="bi bi-chevron-left" /></button>
          <span className="cal-nav-title">{MONTH_NAMES[month]} {year}</span>
          <button className="cal-nav-btn" onClick={nextMonth}><i className="bi bi-chevron-right" /></button>
          <button className="cal-today-btn" onClick={goToday}>Today</button>
          {monthTotals.count > 0 && (
            <div className="cal-summary-pill">
              <strong style={{ color: 'var(--color-text)' }}>{monthTotals.count}</strong> expiring ·{' '}
              <span style={{ color: '#60a5fa' }}><Money value={monthTotals.predRevenue} formatter={fmtMoney} /></span> pred. revenue ·{' '}
              <span style={{ color: '#22d3ee' }}><Money value={monthTotals.predProfit} formatter={fmtMoney} /></span> pred. profit
            </div>
          )}
        </div>

        {/* ── Calendar grid ── */}
        <div className="cal-panel">
          <div className="cal-header">
            {DAY_HEADERS.map(d => <div key={d} className="cal-header-cell">{d}</div>)}
          </div>
          <div className="cal-grid">
            {cells.map((cell, i) => {
              const tiers = cell.current
                ? [...new Set(cell.subs.map(s => getPackageTitle(s.package)))]
                : [];
              const isSelected = cell.current && selected === cell.day;

              return (
                <div
                  key={i}
                  className={[
                    'cal-cell',
                    !cell.current  ? 'other-month' : '',
                    cell.isToday   ? 'today'        : '',
                    isSelected     ? 'selected'     : '',
                  ].join(' ')}
                  onClick={() => cell.current && setSelected(isSelected ? null : cell.day)}
                >
                  <div className="cal-day-num">{cell.day}</div>
                  {cell.subs.length > 0 && (
                    <>
                      <div className="cal-dots">
                        {tiers.slice(0, 6).map(tier => {
                          const sub = cell.subs.find(s => getPackageTitle(s.package) === tier);
                          return (
                            <span
                              key={tier}
                              className="cal-dot"
                              style={{ background: getPackageColor(sub?.package) }}
                              title={tier}
                            />
                          );
                        })}
                      </div>
                      <span className="cal-count-badge">{cell.subs.length}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Selected day detail ── */}
        {selected && selectedSubs.length > 0 && (
          <div className="cal-detail">
            <div className="cal-detail-header">
              <h6 className="cal-detail-title">
                <i className="bi bi-calendar-event" style={{ color: '#3b82f6' }} />
                {selected} {MONTH_NAMES[month]} {year} — {selectedSubs.length} subscription{selectedSubs.length !== 1 ? 's' : ''} expiring
              </h6>
              <button className="cal-detail-close" onClick={() => setSelected(null)}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className="cal-table-scroll">
            <table className="cal-detail-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Package</th>
                  <th>Pred. Revenue</th>
                  <th>Pred. Profit</th>
                  <th>Actual Paid</th>
                  <th>Actual Profit</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {selectedSubs.map(sub => (
                  <tr key={sub._id}>
                    <td style={{ fontWeight: 600, color: 'var(--color-text)' }}>
                      <Username value={accountsMap[sub.accountID] || sub.username || '—'} />
                    </td>
                    <td>
                      <Badge bg="" style={{ background: getPackageColor(sub.package), fontSize: '0.65rem' }}>
                        {getPackageTitle(sub.package)} – {getPackageName(sub.package)}
                      </Badge>
                    </td>
                    <td style={{ color: '#60a5fa' }}><Money value={getPackagePrice(sub.package)} formatter={fmtMoney} /></td>
                    <td style={{ color: '#22d3ee' }}><Money value={getPackageProfit(sub.package)} formatter={fmtMoney} /></td>
                    <td style={{ color: '#34d399' }}><Money value={sub.paidPrice} formatter={fmtMoney} /></td>
                    <td style={{ color: '#c4b5fd' }}><Money value={sub.profitReceived} formatter={fmtMoney} /></td>
                    <td>
                      {sub.active
                        ? <Badge bg="success" style={{ fontSize: '0.62rem' }}>Active</Badge>
                        : <Badge bg="danger"  style={{ fontSize: '0.62rem' }}>Inactive</Badge>
                      }
                    </td>
                    <td>
                      <Link
                        to={`/subscription/view/${sub._id}`}
                        style={{ fontSize: '0.72rem', color: 'var(--color-muted)', textDecoration: 'none', border: '1px solid var(--color-border)', borderRadius: 5, padding: '2px 9px', whiteSpace: 'nowrap' }}
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            <div className="cal-detail-footer">
              <div className="cal-detail-footer-stat">
                <span className="cal-detail-footer-label">Pred. Revenue</span>
                <span className="cal-detail-footer-value" style={{ color: '#60a5fa' }}><Money value={selectedTotals.predRevenue} formatter={fmtMoney} /></span>
              </div>
              <div className="cal-detail-footer-stat">
                <span className="cal-detail-footer-label">Pred. Profit</span>
                <span className="cal-detail-footer-value" style={{ color: '#22d3ee' }}><Money value={selectedTotals.predProfit} formatter={fmtMoney} /></span>
              </div>
              <div className="cal-detail-footer-stat">
                <span className="cal-detail-footer-label">Actual Paid</span>
                <span className="cal-detail-footer-value" style={{ color: '#34d399' }}><Money value={selectedTotals.actRevenue} formatter={fmtMoney} /></span>
              </div>
              <div className="cal-detail-footer-stat">
                <span className="cal-detail-footer-label">Actual Profit</span>
                <span className="cal-detail-footer-value" style={{ color: '#c4b5fd' }}><Money value={selectedTotals.actProfit} formatter={fmtMoney} /></span>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
